import { DetailRowsSkeleton, InlineSkeleton } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { useOrganizationNamesWithStatus } from '@/hooks/use-organizations';
import { dateOnlyOf, formatDateOnly, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetEmployeeInsuranceIds } from '../../hooks/use-profile-view-lookups';
import { useGetSkillsFor, useGetSkillTaxonomy } from '../../hooks/use-skill-lookups';
import {
  experienceMonths,
  formatExperience,
  PROFICIENCY_LEVELS,
  resolveStoredSkill,
  resolveSubCategoryId,
  skillLookupOf,
  storedSkillName,
  toSkillValues,
  toSubCategoryExperienceValues,
} from '../../schemas/skills-schema';
import { Employee } from '../../types/employee.types';
import { InfoCard, InfoRow } from './info-row';

const SEX_LABELS: Record<string, string> = {
  MALE: 'Male',
  FEMALE: 'Female',
  OTHER: 'Other',
  NA: 'Not Specified',
};

const INSURANCE_ROWS = [
  { type: 'Employee', labelKey: 'USER_INSURANCE_ID' },
  { type: 'Spouse', labelKey: 'SPOUSE_INSURANCE_ID' },
  { type: 'Child1', labelKey: 'CHILD_1_INSURANCE_ID' },
  { type: 'Child2', labelKey: 'CHILD_2_INSURANCE_ID' },
  { type: 'Child3', labelKey: 'CHILD_3_INSURANCE_ID' },
] as const;

const VISIBLE_SKILLS = 5;

/** Time since the joining day, as whole years and months. */
const durationSince = (date: string) =>
  formatExperience(experienceMonths(dateOnlyOf(date), '', true));

/** The person's basic info (`UserProfileDetail`): employment, basic information, expertise and insurance. */
export const OverviewTab = ({ employee }: { employee: Employee }) => {
  const { t } = useTranslation();
  const { data: taxonomy, isLoading: isTaxonomyLoading } = useGetSkillTaxonomy();
  const { data: insurance = {}, isLoading: isInsuranceLoading } = useGetEmployeeInsuranceIds(
    employee.UserId
  );
  const [showAllSkills, setShowAllSkills] = useState(false);

  // Each organization's name from its own IAM record, which every employee may read. Not the viewer's
  // `useOrganizations()`: that reads the viewer's IAM user record, which needs `blocks-iam::iam::users`.
  const organizationIds = useMemo(
    () => [
      ...new Set(
        (employee.OrganizationAccess ?? []).map((organization) => organization.OrganizationId)
      ),
    ],
    [employee.OrganizationAccess]
  );
  const { names: organizationNames, isLoading: isOrganizationsLoading } =
    useOrganizationNamesWithStatus(organizationIds);
  const organizationsSummary = isOrganizationsLoading ? (
    <InlineSkeleton className="w-40" />
  ) : (
    organizationIds.map((id) => organizationNames[id]).join(', ')
  );

  const category = employee.CategoryId
    ? (taxonomy?.categories.find((item) => item.ItemId === employee.CategoryId)?.Title ??
      employee.CategoryId)
    : undefined;
  const experiences = toSubCategoryExperienceValues(employee.SubCategoryExperiences).map(
    (entry, index) => {
      const id = resolveSubCategoryId(entry.SubCategoryId, taxonomy?.subCategories ?? []);
      const subCategory = taxonomy?.subCategories.find((item) => item.ItemId === id);
      // The title saved with the entry covers a sub-category that no longer exists.
      const savedTitle = employee.SubCategoryExperiences?.[index]?.SubCategoryId
        ? employee.SubCategoryExperiences[index].SubCategory
        : undefined;
      return {
        ...entry,
        title: subCategory?.Title || savedTitle || entry.SubCategoryId,
        duration: formatExperience(
          experienceMonths(entry.StartDate, entry.EndDate, entry.IsUntilNow)
        ),
      };
    }
  );

  const tenure = employee.JoiningDate ? durationSince(employee.JoiningDate) : undefined;

  const { skillIds, subCategoryIds } = skillLookupOf(employee, taxonomy?.subCategories ?? []);
  const { data: skills = [], isLoading: isSkillsLoading } = useGetSkillsFor(
    skillIds,
    subCategoryIds,
    Boolean(taxonomy)
  );
  // Category and skill names are looked up: until then the raw ids / stored names would show.
  const isExpertiseLoading = isTaxonomyLoading || isSkillsLoading;
  // The skill's current name; one that no longer exists shows the name it was saved under.
  const ownSkills = toSkillValues(employee.Skills).map((entry) => ({
    name: resolveStoredSkill(entry, skills, subCategoryIds)?.Name ?? storedSkillName(entry),
    level: PROFICIENCY_LEVELS.find((level) => level.key === entry.Proficiency)?.title ?? '',
  }));

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <InfoCard title={t('EMPLOYMENT')}>
        <InfoRow label={t('DESIGNATION')} value={employee.Designation} />
        <InfoRow label={t('DEPARTMENT')} value={employee.DepartmentName} />
        <InfoRow label={t('REPORTING_MANAGER')} value={employee.ReportingManagerName} />
        <InfoRow label={t('ORGANIZATIONS')} value={organizationsSummary} />
        <InfoRow label={t('ROLE')} value={(employee.UserRoleKeys ?? []).join(', ')} />
        <InfoRow label={t('JOINING_DATE')} value={formatDateOnly(employee.JoiningDate)} />
        <InfoRow
          label={t('YEARS_OF_EXPERIENCE')}
          value={tenure ? `${tenure.years} ${t('YEARS')} ${tenure.months} ${t('MONTHS')}` : ''}
        />
      </InfoCard>

      <InfoCard title={t('BASIC_INFORMATION')}>
        <InfoRow
          label={t('GENDER')}
          value={employee.Sex ? (SEX_LABELS[employee.Sex] ?? employee.Sex) : ''}
        />
        <InfoRow
          label={t('DATE_OF_BIRTH_CERTIFICATE')}
          value={formatDateOnly(employee.DateOfBirth)}
        />
        <InfoRow
          label={t('DATE_OF_BIRTH_ORIGINAL')}
          value={formatDateOnly(employee.DateOfBirthOriginal)}
        />
      </InfoCard>

      <InfoCard title={t('EXPERTISE')}>
        {isExpertiseLoading ? (
          <DetailRowsSkeleton rows={4} className="py-2" />
        ) : (
          <>
            {!category && experiences.length === 0 && ownSkills.length === 0 && (
              <InfoRow label={t('SUB_CATEGORY')} />
            )}
            {category && <InfoRow label={t('CATEGORY')} value={category} />}
            {experiences.map((entry) => (
              <InfoRow
                key={`${entry.SubCategoryId}-${entry.StartDate}`}
                label={entry.title}
                value={`${formatDateOnly(toDateOnlyValue(entry.StartDate))} - ${
                  entry.IsUntilNow ? t('CURRENT') : formatDateOnly(toDateOnlyValue(entry.EndDate))
                } · ${entry.duration.years} ${t('YEARS')} ${entry.duration.months} ${t('MONTHS')}`}
              />
            ))}
            {(showAllSkills ? ownSkills : ownSkills.slice(0, VISIBLE_SKILLS)).map((skill) => (
              <InfoRow key={skill.name} label={skill.name} value={skill.level} />
            ))}
            {ownSkills.length > VISIBLE_SKILLS && (
              <Button
                variant="link"
                className="h-auto px-0 pt-2"
                onClick={() => setShowAllSkills((shown) => !shown)}
              >
                {showAllSkills
                  ? t('SHOW_LESS')
                  : `+ ${ownSkills.length - VISIBLE_SKILLS} ${t('MORE')}`}
              </Button>
            )}
          </>
        )}
      </InfoCard>

      <InfoCard title={t('INSURANCE_INFORMATION')}>
        {INSURANCE_ROWS.map((row) => (
          <InfoRow
            key={row.type}
            label={t(row.labelKey)}
            value={isInsuranceLoading ? <InlineSkeleton /> : insurance[row.type]}
          />
        ))}
      </InfoCard>
    </div>
  );
};
